import {Component, Inject, OnInit} from "@angular/core";
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef} from "@angular/material/legacy-dialog";
import {TranslateService} from "@ngx-translate/core";
import {ConfirmService} from "@share/component/confirmation/confirm.service";
import {DateApplication, IndemniteDetail} from "@domain/admin/frais/indemnites/frais-mission-detail";
import {PourcentageDomaine} from "@domain/admin/frais/indemnites/indemnites-parametres";
import {filter} from "rxjs/operators";
import {Devise} from "@domain/settings/devise";

/**
 * Données passées à la popup
 */
interface DateApplicationPopinData {
    /** Mode création ou modification */
    isCreation: boolean;
    /** Date d'application existante */
    dateApplication?: DateApplication;
    /** Liste des domaines disponibles */
    listeDomaines: PourcentageDomaine[];
    /** Devise */
    devise: string;
    /** Montants de la date précédente */
    montantsPrecedents?: IndemniteDetail[];
    /** Liste des devises */
    listeDevises: Devise[];
    /** Montant total de la date précédente */
    montantTotalPrecedent?: number;
}

/**
 * Composant popup de gestion d'une date d'application
 */
@Component({
    host: {'data-test-id': 'date-application-popin'},
    selector: 'date-application-popin',
    templateUrl: './date-application-popin.component.html'
})
export class DateApplicationPopinComponent implements OnInit {
    /** Date d'application */
    dateApplication: DateApplication;

    /** Mode création */
    isCreation: boolean;

    /** Liste des indemnités par domaine */
    listeIndemnites: IndemniteDetail[] = [];

    /** Montant total éditable */
    montantTotal: number;

    /** Devise sélectionnée */
    deviseSelectionnee: string;

    /** Indique si au moins une indemnité a un montant */
    hasIndemnite: boolean = false;

    /**
     * Constructeur
     * @param translateService Service de traduction
     * @param matDialogRef Référence vers la popup
     * @param confirmService Service de confirmation
     * @param data Données d'entrée de la popup
     */
    constructor(
        private translateService: TranslateService,
        private matDialogRef: MatDialogRef<DateApplicationPopinComponent>,
        private confirmService: ConfirmService,
        @Inject(MAT_DIALOG_DATA) public data: DateApplicationPopinData
    ) {}

    /**
     * Initialisation
     */
    ngOnInit(): void {
        this.isCreation = this.data.isCreation;

        if (this.isCreation) {
            //Création : nouvelle date avec montants pré-remplis depuis la dernière date
            this.dateApplication = new DateApplication({
                dateApplication: null,
                listeFraisMissionIndemnite: []
            });
            this.initIndemnitesFromDomaines();

            //Initialisation du montant total depuis la date précédente si elle existe
            if (this.data.montantTotalPrecedent != null) {
                this.montantTotal = this.data.montantTotalPrecedent;
            }
        } else {
            //Modification : copie de la date existante
            this.dateApplication = new DateApplication(this.data.dateApplication);

            //Fusionner avec tous les domaines disponibles
            this.mergeIndemnitesWithDomaines();

            //Initialisation du montant total depuis les données existantes
            this.montantTotal = this.dateApplication.montant > 0 ? this.dateApplication.montant : undefined;
        }

        //Vérifie si au moins une indemnité a un montant
        this.hasIndemnite = this.checkHasAtLeastOneIndemnite();

        //Initialisation de la devise sélectionnée
        this.deviseSelectionnee = this.data.devise;
    }

    /**
     * Fusionne les indemnités existantes avec tous les domaines disponibles
     * pour afficher aussi les domaines non encore renseignés
     */
    private mergeIndemnitesWithDomaines(): void {
        const existantes = this.dateApplication.listeFraisMissionIndemnite || [];

        this.listeIndemnites = (this.data.listeDomaines || []).map(domaine => {
            //Recherche de l'indemnité existante pour ce domaine
            const existante = existantes.find(ind => ind.idDomaine === domaine.idDomaine);

            return new IndemniteDetail({
                idDomaine: domaine.idDomaine,
                libelleDomaine: existante?.libelleDomaine || domaine.libelleDomaine,
                pourcentage: existante?.pourcentage ?? null,
                montant: existante?.montant ?? null
            });
        });
    }

    /**
     * Titre de la popup
     *
     * @returns Titre de la popup
     */
    getTitle(): string {
        return this.isCreation
            ? this.translateService.instant('admin.frais.indemnites.fraisMissions.detail.dateApplicationPopin.titleCreation')
            : this.translateService.instant('admin.frais.indemnites.fraisMissions.detail.dateApplicationPopin.titleModification');
    }

    /**
     * Enregistrement
     */
    onSave(): void {
        this.dateApplication.listeFraisMissionIndemnite = this.listeIndemnites;
        this.dateApplication.montant = this.montantTotal;
        this.dateApplication.devise = this.deviseSelectionnee;
        this.matDialogRef.close(this.dateApplication);
    }

    /**
     * Suppression de la date d'application
     */
    onDelete(): void {
        //Demande confirmation avant suppression, puis ferme la popup avec l'action de suppression
        this.confirmService.showConfirm(
            this.translateService.instant('global.suppression.confirmation')
        ).pipe(filter(confirmed => confirmed)).subscribe(() => {
            this.matDialogRef.close({action: 'delete', dateApplication: this.dateApplication});
        });
    }

    /**
     * Initialise les indemnités depuis les domaines avec pré-remplissage
     */
    private initIndemnitesFromDomaines(): void {
        //Si pas de montants précédents (nouveau frais mission), on initialise à 0
        const hasPrecedents = this.data.montantsPrecedents != null && this.data.montantsPrecedents.length > 0;

        this.listeIndemnites = (this.data.listeDomaines || []).map(domaine => {
            //Recherche du montant précédent pour ce domaine
            const precedent = this.data.montantsPrecedents?.find(m => m.idDomaine === domaine.idDomaine);

            return new IndemniteDetail({
                idDomaine: domaine.idDomaine,
                libelleDomaine: domaine.libelleDomaine,
                pourcentage: hasPrecedents ? (precedent?.pourcentage ?? 0) : 0,
                montant: hasPrecedents ? (precedent?.montant ?? null) : null
            });
        });
    }

    /**
     * Recalcule des montants d'indemnité quand le montant total change
     */
    onMontantTotalChange(): void {
        if (this.montantTotal && this.montantTotal > 0) {
            //Recalculer les montants à partir des pourcentages
            for (const ind of this.listeIndemnites) {
                if (ind.pourcentage != null && ind.pourcentage > 0) {
                    ind.montant = Math.round(this.montantTotal * ind.pourcentage / 100 * 100) / 100;
                }
            }
        }
    }

    /**
     * Recalcule du montant quand un pourcentage change
     */
    onPourcentageChange(indemnite: IndemniteDetail): void {
        if (this.montantTotal && this.montantTotal > 0 && indemnite.pourcentage != null) {
            //Conversion du pourcentage
            const pourcentage = Number(String(indemnite.pourcentage).replace(/[,]/g, '.'));

            //Calcul du montant à partir du pourcentage si valide
            if (!isNaN(pourcentage)) {
                indemnite.montant = Math.round(this.montantTotal * pourcentage / 100 * 100) / 100;
            }
        }

        //Vérifie si au moins une indemnité a un montant
        this.hasIndemnite = this.checkHasAtLeastOneIndemnite();
    }

    /**
     * Recalcule du pourcentage quand un montant d'indemnité change
     */
    onMontantIndemniteChange(indemnite: IndemniteDetail): void {
        if (this.montantTotal && this.montantTotal > 0 && indemnite.montant != null) {
            //Conversion du montant
            const montant = this.parseValeurNumerique(indemnite.montant);

            //Calcul du pourcentage à partir du montant si valide
            if (!isNaN(montant)) {
                indemnite.pourcentage = Math.round(montant / this.montantTotal * 100 * 100) / 100;
            } else {
                indemnite.pourcentage = null;
            }
        } else {
            indemnite.pourcentage = null;
        }

        //Vérifie si au moins une indemnité a un montant
        this.hasIndemnite = this.checkHasAtLeastOneIndemnite();
    }

    /**
     * Convertit une valeur saisie en nombre (virgule FR, espaces, nbsp)
     */
    private parseValeurNumerique(valeur: number | string | null | undefined): number | null {
        //Valeur vide => non renseignée
        if (valeur == null || valeur === '') {
            return null;
        }

        //Nettoyage des espaces + remplacement virgule par point
        const normalisee = String(valeur).replace(/[\s\u00A0]/g, '').replace(/,/g, '.');
        const nombre = Number(normalisee);

        //Valeur non numérique
        return isNaN(nombre) ? null : nombre;
    }

    /**
     * Changement de devise
     */
    onDeviseChange(devise: string): void {
        //Mise à jour de la devise sélectionnée
        this.deviseSelectionnee = devise;

        //Synchronisation avec l'objet métier renvoyé au parent
        this.dateApplication.devise = devise;
    }

    /**
     * Vérifie si au moins une indemnité a un montant
     */
    checkHasAtLeastOneIndemnite(): boolean {
        return this.listeIndemnites?.some(ind => {
            const montant = this.parseValeurNumerique(ind.montant);
            return montant != null && montant > 0;
        }) ?? false;
    }

    /**
     * Calcule le pourcentage total des indemnités
     */
    get pourcentageTotal(): number {
        return this.listeIndemnites?.reduce((sum, ind) => sum + (Number(ind.pourcentage) || 0), 0) || 0;
    }

    /**
     * Vérifie si au moins un pourcentage est renseigné
     */
    get hasPourcentage(): boolean {
        return this.listeIndemnites?.some(ind => ind.pourcentage != null && ind.pourcentage > 0) ?? false;
    }

    /**
     * Calcule le montant total des indemnités
     */
    get montantTotalIndemnites(): number {
        return this.listeIndemnites?.reduce((sum, ind) => sum + (Number(ind.montant) || 0), 0) || 0;
    }

}